import React, { FC } from "react";
import styles from "./Dashboard.module.scss";
import Payment from "../old/Payment/Payment";
import EventDetails, { EventResources } from "../EventDetails/EventDetails";
import Profile from "../Profile/Profile";
import MusicTheory from "../MusicTheory/MusicTheory";
import Admin, { hasVisibleSpecialActions } from "../Admin/Admin";
import Attendance from "../SelfAttendance/Attendance";
import SubscriptionBanner from "./SubscriptionBanner";
import { EventAttendance } from "../SelfAttendance/SelfAttendance";
import apiInstance from "../services/httpService";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import MusicNoteOutlinedIcon from "@mui/icons-material/MusicNoteOutlined";
import AdminPanelSettingsOutlinedIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";

interface DashboardProps {
  permissions?: Record<string, boolean>;
}

const SECTION_LABELS: Record<string, string> = {
  "events":         "Events",
  "profile":        "My Profile",
  "music-theory":   "Music Theory",
  "self-attendance":"Attendance",
  "admin":          "Special Actions",
};

/** A practice is markable while it is scheduled or in progress. */
const ACTIVE_STATUSES = ["scheduled", "in_progress"];

/**
 * A guest is identified as a user whose *only* granted permission is
 * `view_special_guidelines`. Guests do not see the Music Theory tile.
 */
const isGuestUser = (permissions?: Record<string, boolean>): boolean => {
  if (!permissions) return false;
  const granted = Object.keys(permissions).filter((k) => permissions[k] === true);
  return granted.length === 1 && granted[0] === "view_special_guidelines";
};

const Dashboard: FC<DashboardProps> = ({ permissions }) => {
  const [selectedEventDetails, setSelectedEventDetails] = React.useState(
    {} as EventResources
  );
  const [isEventSelected, setIsEventSelected] = React.useState(false);
  const [activeSection, setActiveSection]     = React.useState<string | null>(null);
  const [subSectionLabel, setSubSectionLabel]       = React.useState<string | null>(null);
  const [subSubSectionLabel, setSubSubSectionLabel] = React.useState<string | null>(null);
  /** Incrementing these keys remounts the section, resetting its internal state. */
  const [musicTheoryKey, setMusicTheoryKey]   = React.useState(0);
  const [adminKey, setAdminKey]               = React.useState(0);
  const [attendanceKey, setAttendanceKey]     = React.useState(0);
  /** Incremented to remount ViewUser back to its list (without losing the admin subtile context). */
  const [viewUserKey, setViewUserKey]         = React.useState(0);
  const [open, setOpen]       = React.useState(false);
  const [severity, setSeverity] = React.useState("success");
  const [message, setMessage] = React.useState("");
  const [selfAttendance, setSelfAttendance] = React.useState<EventAttendance[]>([]);
  const [hasActivePractice, setHasActivePractice] = React.useState(false);

  // Load the user's own attendance (only if they can view it) to decide whether
  // to show the Attendance tile / "My Attendance" view.
  React.useEffect(() => {
    if (!permissions?.view_self_attendance) return;
    const token = localStorage.getItem("token");
    apiInstance
      .axiosCall("api/ccc/practice/attendance/self", { token }, "POST")
      .then((res: any) => {
        if (res?.data?.status === "success") setSelfAttendance(res.data.data || []);
      })
      .catch(() => setSelfAttendance([]));
  }, [permissions]);

  // Mark Attendance lives inside the Attendance tile and is only offered while a
  // practice is active, so check for one when the user may mark attendance.
  React.useEffect(() => {
    if (!permissions?.mark_overall_attendance) return;
    const token = localStorage.getItem("token");
    apiInstance
      .axiosCall("api/ccc/practice/active", { token }, "POST")
      .then((res: any) => {
        const d = res?.data?.data;
        setHasActivePractice(
          res?.data?.status === "success" && !!d && ACTIVE_STATUSES.includes(d.practice_status)
        );
      })
      .catch(() => setHasActivePractice(false));
  }, [permissions]);

  const Alert = React.forwardRef(function Alert(props: any, ref: any) {
    return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
  });

  const handleClose = (_event: any, reason: any) => {
    if (reason === "clickaway") return;
    setOpen(false);
  };

  const eventSelected = (eventId: number) => {
    const reqData = {
      token: localStorage.getItem("token"),
      data: { event_id: eventId },
    };
    apiInstance
      .axiosCall("api/ccc/user/event_resource", reqData, "POST")
      .then((response: any) => {
        if (response.data.status == "success") {
          setIsEventSelected(true);
          setSelectedEventDetails(response.data.data);
        } else {
          setSeverity("error");
          setMessage(response.data.message);
          setOpen(true);
        }
      })
      .catch((error: any) => {
        console.error("Event resource API failed:", error);
        setSeverity("error");
        setMessage("Could not load resources. Please try again.");
        setOpen(true);
      });
  };

  /** Navigate all the way back to the tile home screen. */
  const goHome = () => {
    setIsEventSelected(false);
    setActiveSection(null);
    setSelectedEventDetails({} as EventResources);
    setSubSectionLabel(null);
    setSubSubSectionLabel(null);
  };

  /** Navigate back to the current section's top level (clears sub-section). */
  const goToSection = () => {
    setSubSectionLabel(null);
    setSubSubSectionLabel(null);
    setMusicTheoryKey((k) => k + 1);  // remounts MusicTheory → resets to subtile grid
    setAdminKey((k) => k + 1);        // remounts Admin → resets to subtile grid
    setAttendanceKey((k) => k + 1);   // remounts Attendance → resets to subtile grid
  };

  /** Navigate back to the first sub-section level (e.g. View User list from Details). */
  const goToSubSection = () => {
    setSubSubSectionLabel(null);
    setViewUserKey((k) => k + 1);  // remounts ViewUser → resets to list
  };

  const sectionLabel = activeSection ? (SECTION_LABELS[activeSection] ?? activeSection) : null;

  // Mark Attendance is available when the user can mark and a practice is active.
  const canMarkAttendance =
    (permissions?.mark_overall_attendance ?? false) && hasActivePractice;
  // Summary is available to anyone with mark_overall_attendance, regardless of
  // whether a practice is currently active.
  const canViewSummary = permissions?.mark_overall_attendance ?? false;
  // The Attendance tile shows when the user can view their own attendance, can
  // mark attendance, or can view the summary.
  const showAttendance =
    ((permissions?.view_self_attendance ?? false) && selfAttendance.length > 0) ||
    canMarkAttendance ||
    canViewSummary;

  return (
    <div className={styles.Dashboard} data-testid="Dashboard">

      {/* Subscription status — persistent, always above the breadcrumb */}
      {!isGuestUser(permissions) && <SubscriptionBanner />}

      {/* Breadcrumb */}
      <nav className={styles.Dashboard__breadcrumb}>
        {activeSection === null ? (
          <span className={`${styles.Dashboard__crumb} ${styles.Dashboard__crumb_active}`}>
            Home
          </span>
        ) : (
          <>
            <span className={styles.Dashboard__crumb} onClick={goHome}>
              Home
            </span>

            {subSectionLabel ? (
              <span className={styles.Dashboard__crumb} onClick={goToSection}>
                {sectionLabel}
              </span>
            ) : (
              <span className={`${styles.Dashboard__crumb} ${styles.Dashboard__crumb_active}`}>
                {sectionLabel}
              </span>
            )}

            {subSectionLabel && (
              subSubSectionLabel ? (
                <span className={styles.Dashboard__crumb} onClick={goToSubSection}>
                  {subSectionLabel}
                </span>
              ) : (
                <span className={`${styles.Dashboard__crumb} ${styles.Dashboard__crumb_active}`}>
                  {subSectionLabel}
                </span>
              )
            )}

            {subSubSectionLabel && (
              <span className={`${styles.Dashboard__crumb} ${styles.Dashboard__crumb_active}`}>
                {subSubSectionLabel}
              </span>
            )}
          </>
        )}
      </nav>

      {activeSection === "events" && isEventSelected ? (
        <EventDetails selectedEventDetails={{ ...selectedEventDetails }} />
      ) : activeSection === "events" ? (
        <Payment eventSelected={eventSelected} />
      ) : activeSection === "profile" ? (
        <Profile />
      ) : activeSection === "music-theory" ? (
        <MusicTheory
          key={musicTheoryKey}
          onSubSectionChange={setSubSectionLabel}
        />
      ) : activeSection === "self-attendance" ? (
        <Attendance
          key={attendanceKey}
          data={selfAttendance}
          canMark={canMarkAttendance}
          canViewSummary={canViewSummary}
          onSubSectionChange={setSubSectionLabel}
        />
      ) : activeSection === "admin" ? (
        <Admin
          key={adminKey}
          permissions={permissions}
          onSubSectionChange={setSubSectionLabel}
          onSubSubSectionChange={setSubSubSectionLabel}
          viewUserKey={viewUserKey}
        />
      ) : (
        <Tabs
          setActiveSection={setActiveSection}
          permissions={permissions}
          showAttendance={showAttendance}
        />
      )}

      {/* Toast */}
      <Snackbar open={open} autoHideDuration={5000} onClose={handleClose}>
        <Alert onClose={handleClose} severity={severity} sx={{ width: "100%" }}>
          {message}
        </Alert>
      </Snackbar>
    </div>
  );
};

const Tabs: FC<any> = ({ setActiveSection, permissions, showAttendance }) => {
  return (
    <section className="d-flex gap-5 mx-5 my-5 flex-wrap">
      <div
        className={styles.Dashboard__tab}
        onClick={() => setActiveSection("events")}
      >
        <EventOutlinedIcon className={styles.Dashboard__tab_icon} />
        Events
      </div>
      {!isGuestUser(permissions) && (
        <div
          className={styles.Dashboard__tab}
          onClick={() => setActiveSection("music-theory")}
        >
          <MusicNoteOutlinedIcon className={styles.Dashboard__tab_icon} />
          Music Theory
        </div>
      )}
      {showAttendance && (
        <div
          className={styles.Dashboard__tab}
          onClick={() => setActiveSection("self-attendance")}
        >
          <FactCheckOutlinedIcon className={styles.Dashboard__tab_icon} />
          Attendance
        </div>
      )}
      <div
        className={styles.Dashboard__tab}
        onClick={() => setActiveSection("profile")}
      >
        <PersonOutlinedIcon className={styles.Dashboard__tab_icon} />
        My Profile
      </div>
      {hasVisibleSpecialActions(permissions) && (
        <div
          className={styles.Dashboard__tab}
          onClick={() => setActiveSection("admin")}
        >
          <AdminPanelSettingsOutlinedIcon className={styles.Dashboard__tab_icon} />
          Special Actions
        </div>
      )}
    </section>
  );
};

export default Dashboard;
